/**
 * QuickActionsFab — floating action button with fan-out quick actions.
 * Mounted globally for authenticated users; navigates to create flows on tap.
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { isFeatureEnabled } from '../lib/featureFlags';
import { useFabClearance, useQuickActionsReplaced } from '../hooks/useFabClearance';
import { useAnyModalOpen } from '../hooks/useModalOpen';
import { QUICK_FAB, quickFabBottom } from '../lib/fabStack';

const ACTIONS = [
  { icon: '📝', label: 'New Post', path: '/feed?compose=1', flagKey: null },         // always on
  { icon: '🏠', label: 'New Listing', path: '/listings/new', flagKey: 'listings' },
  // RW-1524: the Transactions module retired — deals are born in One Pipeline.
  { icon: '📋', label: 'New Deal', path: '/deals/new', flagKey: 'onePipeline' },
  // RW-3307: no "Send for signature" / "Fill a form" — signature requests start
  // on a deal's Documents tab (DocuSeal); Rêve Sign only lists them.
  { icon: '👤', label: 'New Buyer', path: '/buyers', flagKey: 'buyers' },
  { icon: '✉️', label: 'New Message', path: '/messages', flagKey: null },             // always on
  { icon: '📅', label: 'New Event', path: '/events', flagKey: 'events' },
  { icon: '🥷', label: 'Ninja FLOW', path: '/ninja-flow', flagKey: 'ninjaFlow' },
];

export default function QuickActionsFab({ user, mobile, isAdmin }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  // RW-1486: pages with a bottom-pinned composer (Ask Rêve) register it; the
  // FAB lifts above it instead of covering the Send button.
  const fabLift = useFabClearance();
  // RW-2953: step aside while any modal is open (it used to sit on the
  // composer's Post button), and wherever a page's own create "+" replaces
  // this one — on the phone feed there is one "+", not two stacked.
  const modalOpen = useAnyModalOpen();
  const replaced = useQuickActionsReplaced();
  const hidden = modalOpen || replaced;
  useEffect(() => { if (hidden) setOpen(false); }, [hidden]);
  const { flags } = useFeatureFlags();
  const visibleActions = useMemo(() => ACTIONS.filter((a) => isFeatureEnabled(a.flagKey, flags, isAdmin)), [flags, isAdmin]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  if (!user || hidden) return null;

  // Lift above the mobile nav, composing with the iOS home-indicator inset —
  // and above any registered bottom-pinned bar (RW-1486), whichever is taller.
  // RW-2269: the offsets live in src/lib/fabStack.js so the three corner
  // buttons cannot drift apart (the feed's create FAB registers itself as an
  // obstruction, which is what lifts this one clear of it on the feed).
  const bottomOffset = mobile
    ? `max(calc(${QUICK_FAB.bottomMobile}px + var(--reve-safe-bottom, 0px)), ${quickFabBottom(fabLift, true)}px)`
    : quickFabBottom(fabLift, false);

  return (
    <>
      <style>{`
        @keyframes reve-fab-in {
          from { transform: translateY(20px) scale(0.8); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        .reve-fab-action {
          animation: reve-fab-in 200ms cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
        }
      `}</style>
      <div ref={ref} style={{ position: 'fixed', right: QUICK_FAB.right, bottom: bottomOffset, zIndex: 900, transition: 'bottom 200ms ease' }}>
        {/* Expanded actions */}
        {open ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
            {visibleActions.map((a, i) => (
              <button
                key={a.label} /* RW-1557: two actions share /transactions — path is no longer unique */
                className="reve-fab-action"
                onClick={() => go(a.path)}
                style={{
                  animationDelay: `${i * 50}ms`,
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                <span style={{
                  background: 'rgba(17,24,39,0.85)', color: '#fff',
                  padding: '6px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                  whiteSpace: 'nowrap', fontFamily: "'Jost', sans-serif",
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>{a.label}</span>
                <span style={{
                  width: 44, height: 44, borderRadius: '50%', background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>{a.icon}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* Main FAB */}
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close quick actions' : 'Open quick actions'}
          aria-expanded={open}
          style={{
            width: QUICK_FAB.size, height: QUICK_FAB.size, borderRadius: '50%',
            background: '#E8836A', color: '#fff', border: 'none',
            fontSize: 28, fontWeight: 300, cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: open ? 'rotate(45deg)' : 'rotate(0)',
            transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            fontFamily: 'sans-serif',
          }}
        >
          +
        </button>
      </div>
    </>
  );
}
